import type { ReactNode } from 'react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

/**
 * The bar at the top of every page.
 *
 * One component so the chrome — padding, border, background, the mobile sidebar
 * trigger — is defined once. Before this, twenty pages each repeated
 * `px-6 py-4 border-b border-border bg-background`, which meant restyling the
 * header meant a twenty-file sweep and any page that drifted stayed drifted.
 *
 * The background is the same gradient as the home page hero, built from the
 * `--hero-*` tokens so it follows light/dark like everything else.
 *
 * Layout is title (plus anything inline with it) on the left, actions on the
 * right. Pages whose header is genuinely different can still pass `className`
 * or drop their own markup into `children`.
 */
/**
 * The shared surface, defined once as `.page-header-surface` in index.css.
 * Exported for the handful of pages whose header layout is bespoke enough that
 * they can't use this component but still need to match it.
 */
export const PAGE_HEADER_SURFACE = 'page-header-surface';

export function PageHeader({
  title,
  description,
  actions,
  children,
  className,
}: {
  /** Usually a string; a node when a badge or count sits next to the title. */
  title: ReactNode;
  description?: ReactNode;
  /** Right-hand controls: filters, search, primary buttons. */
  actions?: ReactNode;
  /** Extra content below the title row (tabs, breadcrumbs, filter bars). */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'w-full px-6 py-4 flex-shrink-0 z-10',
        PAGE_HEADER_SURFACE,
        className,
      )}
    >
      <div className="flex items-center justify-between w-full gap-4">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            {typeof title === 'string' ? (
              <h1 className="text-xl font-semibold text-foreground truncate">
                {title}
              </h1>
            ) : (
              title
            )}
          </div>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>
        )}
      </div>
      {children}
    </div>
  );
}
